import { useCallback, useMemo, useState } from 'react';
import { MAX_RELEASES_TO_SHOW, PRUNE_DAYS } from '../utils';

const STORAGE_KEY = 'releases';
export interface ReleaseItem {
  releaseId: string;
  releaseDate: string;
}

function parseStored(raw: string | null): ReleaseItem[] {
  if (raw == null || raw === '') return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is ReleaseItem =>
        item != null &&
        typeof item === 'object' &&
        typeof (item as ReleaseItem).releaseId === 'string' &&
        typeof (item as ReleaseItem).releaseDate === 'string',
    );
  } catch {
    return [];
  }
}

function isOlderThan30Days(releaseDate: string): boolean {
  const date = new Date(releaseDate);
  if (Number.isNaN(date.getTime())) return true;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - PRUNE_DAYS);
  return date < cutoff;
}

function pruneStoredBy30Days(stored: ReleaseItem[]): ReleaseItem[] {
  return stored.filter((item) => !isOlderThan30Days(item.releaseDate));
}

function readStored(): ReleaseItem[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const parsed = parseStored(raw);
  return pruneStoredBy30Days(parsed);
}

export interface ReleaseItemInput {
  releaseId: string;
  releaseDate: string | Date;
}

function toReleaseItem(r: ReleaseItemInput): ReleaseItem {
  return {
    releaseId: r.releaseId,
    releaseDate:
      typeof r.releaseDate === 'string'
        ? r.releaseDate
        : r.releaseDate.toISOString(),
  };
}

export function useWhatsNewModalLocalStorage(releases: ReleaseItemInput[]) {
  const [stored, setStored] = useState<ReleaseItem[]>(() => readStored());

  const releaseIdsToShow = useMemo(() => {
    const storedIds = new Set(stored.map((s) => s.releaseId));
    const unseen = releases.filter((r) => !storedIds.has(r.releaseId));
    return unseen.map((r) => r.releaseId).slice(0, MAX_RELEASES_TO_SHOW);
  }, [releases, stored]);

  const isVisible = releaseIdsToShow.length > 0;

  const markAsSeen = useCallback(
    (releaseIds: string[]) => {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const byId = new Map(stored.map((item) => [item.releaseId, item]));
      const releasesById = new Map(
        releases.map((r) => [r.releaseId, toReleaseItem(r)]),
      );
      for (const id of releaseIds) {
        const release = releasesById.get(id);
        if (release) byId.set(id, release);
      }
      const merged = Array.from(byId.values());
      const pruned = pruneStoredBy30Days(merged);
      setStored(pruned);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
    },
    [releases, stored],
  );

  return { isVisible, releaseIdsToShow, markAsSeen };
}
